import { useEffect, useState } from 'react';
import Axios from 'axios';
import { CreateTriggerWorkflow, LinkCellRenderer, DockContainer } from '@components';
import { Api } from '@services';
import { handleTimeoutApiError } from '../../utils/handleTimeoutApiError';
import { Row, Col, Card } from 'react-bootstrap';
import { PagedModel } from '../../models/PagedModel';
import { triggerColDefs } from './columnDefs';
import { useDock } from '@react';
import { PagedQueryParams, ServerSideGrid } from '../../components/grids/AgGrid/ServerSideGrid';
import { useParams } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import TriggerResolutionModal from './TriggerResolutionModal';
import { TriggerSeverityLevels } from '../../enums/TriggerSeverityLevels';

export function TriggerListContainer<T>() {
  //useState Hooks
  const { id, instanceId, resolvedById, appId } = useParams<any>();
  const [rowData, setRowData] = useState<any>(new PagedModel<T>());
  const [csvData, setCsvData] = useState(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { renderDock, closeDock } = useDock();
  const [showModal, setShowModal] = useState(false);
  const [rowSelected, setRowSelected] = useState(null);

  const csvFileName = 'Triggers_Export';
  const history = useHistory();
  const cancelToken = Api.CancelToken;
  let fetchDataCancel;

  const openTrigger = (triggerId: number) => {
    renderDock({
      render: () => (
        <DockContainer>
          <CreateTriggerWorkflow
            triggerId={triggerId}
            onDone={() => {
              closeDock();
              history.push('/triggers');
            }}
            setRowSelected={setRowSelected}
          />
        </DockContainer>
      ),
    });
  };

  useEffect(() => {
    history.listen((location) => {
      const url = location.pathname.split('/');
      let triggerId = +url[url.length - 1];
      if (!isNaN(triggerId) && history.location.pathname.includes(`/triggers/${triggerId}`)) {
        openTrigger(triggerId);
      }
    });
  }, [history]);

  useEffect(() => {
    if (id) {
      openTrigger(id);
    }

    if (instanceId) {
      setShowModal(true);
    }
  }, []);

  const fetchData = async (payload?, csvExport?, queryState?) => {
    if (fetchDataCancel !== null && typeof fetchDataCancel === 'function') {
      fetchDataCancel();
      fetchDataCancel = null;
    }
    setIsLoading(true);

    Api.get<PagedModel<T>>(
      `bulktriggers?top=${queryState.pageSize}&skip=${queryState.skip()}&filter=${
        queryState.filter
      }&orderBy=${queryState.orderBy()}`,
      {
        cancelToken: new cancelToken(function executor(c) {
          fetchDataCancel = c;
        }),
      },
    )
      .then((data) => {
        let resp = data.data;
        setRowData(resp);
        setIsLoading(false);
      })
      .catch((e) => {
        if (!Axios.isCancel(e)) {
          setIsLoading(false);
        }
      });
    // Help create call to export Triggers
    if (csvExport) {
      const config = {
        responseType: 'blob',
        headers: {
          Accept: 'text/csv',
        },
      };

      Api.get(
        `/bulktriggers/export?${
          queryState.filter ? `search=${queryState.filter}` : ''
        }${`&orderBy=${queryState.orderBy()}`}`,
        config,
      )
        .then((res) => {
          setCsvData(res.data);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error(error);
          handleTimeoutApiError(error);
          setIsLoading(false);
        });
    }
  };

  return (
    <>
      <TriggerResolutionModal
        show={showModal}
        handleClose={() => {
          setShowModal(!showModal);
          history.push('/triggers' + window.location.search);
        }}
        instanceId={instanceId}
        resolvedById={resolvedById}
        appId={appId}
      />

      <ServerSideGrid
        id={'Triggers'}
        columnDefs={triggerColDefs}
        initQueryParams={new PagedQueryParams(id)}
        handleFetchData={fetchData}
        rowData={rowData}
        csvData={csvData}
        csvFileName={csvFileName}
        isLoading={isLoading}
        disabledFilters={[]}
        filterControl={false}
        searchControl={true}
        viewingControl={true}
        pagerControl={true}
        exportControl={true}
        enumTranslation={{
          severity: TriggerSeverityLevels,
        }}
        gridProps={{
          frameworkComponents: {
            LinkCellRenderer,
          },
        }}
        deleteRow={rowSelected}
      />
    </>
  );
}
